import React, { useState } from 'react';
import { Clock, ArrowRight, Calendar, BarChart2, Cpu, History } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой конвертации
 */
const QuickConversionSidebar = ({ 
  formData,
  setFormData,
  handleConvert,
  russianTimezones,
  historicalDates,
  currentTime
}) => {
  const [category, setCategory] = useState('all');
  
  // Обновление текущего времени
  const currentUnixTime = Math.floor(currentTime / 1000);
  
  // Функция быстрой конвертации текущего времени
  const convertCurrentTime = () => {
    setFormData(prev => ({
      ...prev,
      conversionType: 'unix-to-human',
      unixTimestamp: currentUnixTime.toString()
    }));
    
    handleConvert({
      ...formData,
      conversionType: 'unix-to-human',
      unixTimestamp: currentUnixTime.toString()
    });
  };
  
  // Функция быстрой конвертации исторической даты
  const convertHistoricalDate = (timestamp) => {
    setFormData(prev => ({
      ...prev,
      conversionType: 'unix-to-human',
      unixTimestamp: timestamp.toString()
    }));
    
    handleConvert({
      ...formData,
      conversionType: 'unix-to-human',
      unixTimestamp: timestamp.toString()
    });
  };
  
  // Функция быстрой конвертации произвольной даты
  const convertCustomDate = (date) => {
    const timestamp = Math.floor(new Date(date).getTime() / 1000);
    
    setFormData(prev => ({
      ...prev,
      conversionType: 'unix-to-human',
      unixTimestamp: timestamp.toString()
    }));
    
    handleConvert({
      ...formData,
      conversionType: 'unix-to-human',
      unixTimestamp: timestamp.toString()
    });
  };
  
  // Фильтрация исторических дат по категории
  const filteredDates = category === 'all' 
    ? historicalDates 
    : historicalDates.filter(date => date.category === category);
  
  // Список предопределенных важных дат
  const predefinedDates = [
    { label: 'Начало 2000-го года', timestamp: 946684800 },
    { label: 'Начало Unix-эпохи', timestamp: 0 },
    { label: '1 миллиард секунд', timestamp: 1000000000 },
    { label: 'Y2K38 (предел 32-бит)', timestamp: 2147483647 }
  ];
  
  // Текущие специальные даты
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000;
  const startOfYear = new Date(now.getFullYear(), 0, 1).getTime() / 1000;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-cyan-500" />
        Быстрая конвертация
      </h3>
      
      <div className="space-y-4">
        {/* Текущее время */}
        <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-cyan-700 dark:text-cyan-300 flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              Текущее время
            </h4>
            <span className="text-xs font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded text-cyan-600 dark:text-cyan-400">
              {currentUnixTime}
            </span>
          </div>
          <Button
            fullWidth
            size="sm"
            onClick={convertCurrentTime}
            icon={<ArrowRight className="w-3 h-3" />}
            variant="light"
          >
            Конвертировать текущее время
          </Button>
        </div>
        
        {/* Специальные даты */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-cyan-500" />
            Специальные даты
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => convertCustomDate(now)}
              className="p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              Сегодня
            </button>
            <button
              type="button"
              onClick={() => convertCustomDate(new Date(startOfDay * 1000))}
              className="p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              Начало дня
            </button>
            <button
              type="button"
              onClick={() => convertCustomDate(new Date(startOfMonth * 1000))}
              className="p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              Начало месяца
            </button>
            <button
              type="button"
              onClick={() => convertCustomDate(new Date(startOfYear * 1000))}
              className="p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              Начало года
            </button>
          </div>
        </div>
        
        {/* Выбор предопределенных дат */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Cpu className="w-4 h-4 mr-1 text-cyan-500" />
            Важные даты Unix
          </h4>
          <div className="space-y-2">
            {predefinedDates.map((date, index) => (
              <button
                key={index}
                type="button"
                onClick={() => convertHistoricalDate(date.timestamp)}
                className="w-full p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex justify-between items-center"
              >
                <span>{date.label}</span>
                <code className="font-mono text-cyan-600 dark:text-cyan-400">{date.timestamp}</code>
              </button>
            ))}
          </div>
        </div>
        
        {/* Исторические даты России */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <History className="w-4 h-4 mr-1 text-cyan-500" />
              Исторические даты России
            </h4>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="text-xs p-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <option value="all">Все</option>
              <option value="politics">Политика</option>
              <option value="economy">Экономика</option>
              <option value="technology">Технологии</option>
              <option value="sports">Спорт</option>
              <option value="media">Медиа</option>
            </select>
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1 history-table">
            {filteredDates.map((date, index) => (
              <button
                key={index}
                type="button"
                onClick={() => convertHistoricalDate(date.timestamp)}
                className="w-full p-2 text-xs rounded border bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-left hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="font-medium text-gray-800 dark:text-gray-200">{date.description}</div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-gray-500 dark:text-gray-400">
                    {new Date(date.timestamp * 1000).toLocaleDateString('ru-RU')}
                  </span>
                  <code className="font-mono text-cyan-600 dark:text-cyan-400">{date.timestamp}</code>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickConversionSidebar;